import { useState, useEffect } from 'react';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';

interface SplashScreenProps {
  t: Theme;
  onDone: () => void;
}

export function SplashScreen({ t, onDone }: SplashScreenProps) {
  const [phase, setPhase] = useState(0); // 0: logo only, 1: tagline + dots, 2: exit

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 900);
    const t2 = setTimeout(() => setPhase(2), 2100);
    const t3 = setTimeout(() => onDone(), 2600);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onDone]);

  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 400,
      background: t.paper,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden',
      opacity: phase === 2 ? 0 : 1,
      transition: 'opacity 480ms ease',
      pointerEvents: phase === 2 ? 'none' : 'auto',
    }}>
      {/* Ambient orbs */}
      <div style={{
        position: 'absolute', top: '-15%', right: '-20%',
        width: 300, height: 300, borderRadius: '100%',
        background: `radial-gradient(circle, ${t.blush} 0%, transparent 65%)`,
        opacity: 0.45,
        animation: 'pmBreathe 3.2s ease-in-out infinite',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-12%', left: '-18%',
        width: 260, height: 260, borderRadius: '100%',
        background: `radial-gradient(circle, ${t.gold} 0%, transparent 65%)`,
        opacity: 0.3,
        animation: 'pmBreathe 3.2s ease-in-out 0.6s infinite',
        pointerEvents: 'none',
      }} />

      {/* Logo */}
      <div style={{
        fontFamily: FONTS.display, fontStyle: 'italic', fontWeight: 400,
        fontSize: 44, color: t.ink,
        animation: 'pmLogoReveal 900ms cubic-bezier(0.2,0.8,0.2,1) both',
      }}>
        <span style={{ color: t.terracotta }}>p</span>reminade
      </div>

      {/* Tagline */}
      <div style={{
        marginTop: 18,
        textAlign: 'center',
        padding: '0 28px',
        opacity: phase >= 1 ? 1 : 0,
        transform: phase >= 1 ? 'translateY(0)' : 'translateY(12px)',
        transition: 'opacity 550ms ease, transform 550ms cubic-bezier(0.2,0.8,0.2,1)',
      }}>
        <div style={{
          fontFamily: FONTS.display, fontWeight: 400,
          fontSize: 38, lineHeight: 1.12,
          letterSpacing: -0.5, color: t.ink,
        }}>
          Find your person.
        </div>
        <div style={{
          fontFamily: FONTS.display, fontStyle: 'italic', fontWeight: 400,
          fontSize: 38, lineHeight: 1.12,
          letterSpacing: -0.5, color: t.terracotta,
        }}>
          Find your People.
        </div>
      </div>

      {/* Bouncing dots */}
      <div style={{ position: 'absolute', bottom: 70, display: 'flex', gap: 7 }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{
            width: 7, height: 7, borderRadius: '100%',
            background: t.terracotta,
            animation: `pmDotBounce 1.2s ease-in-out ${i * 0.15}s infinite`,
          }} />
        ))}
      </div>
    </div>
  );
}
